<template>
  <div class="ma-4">
    <v-card v-if="!dataLoaded">
      <v-card-title>Academic Program/Advisor Change Form (Loading...)</v-card-title>
    </v-card>
    <change-status v-else-if="'program' in submission && submission.program.length > 0 && status !== 'Returned'" :submission="submission" :term-label="termLabel" @update="(data) => submission = data"></change-status>
    <template v-else>
      <v-toolbar>
        <v-toolbar-title>Academic Program/Advisor Change: {{ termLabel }}</v-toolbar-title>
      </v-toolbar>
      <v-card class="mb-2">
        <v-card-text>
          <p>The top portion of this form contains your current program information: majors (with advisors and any concentrations), minors, and if you're planning on doing a teacher certificate. Below that is where you can choose an option for what you want to change.</p>
          <p>Please note that while you can request to change your advisor, you might be assigned a different advisor based on if the requested advisor is going on sabbatical, or to keep the department's advising duties balanced between professors, or based on a system developed by the department of your major.</p>
        </v-card-text>
      </v-card>
      <v-row class="my-2">
        <v-col v-for="{ major, advisors, concentrations, allConcentrations } of current.programs" :key="major">
          <v-card>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Major</v-list-item-subtitle>
                    <v-list-item-title>{{ getDesc('major', major) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Advisor{{ advisors.length > 1 ? 's' : '' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ text, value } in advisors" :key="'advr-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="concentrations.length > 0 || allConcentrations.length > 0">
                  <v-list-item-subtitle v-if="concentrations.length === 0">No Concentrations Selected</v-list-item-subtitle>
                  <v-list-item-content v-else>
                    <v-list-item-subtitle>Concentration{{ concentrations.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ text, value } of concentrations" :key="'conc-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <confirm-dialog v-if="current.programs.length > 1" title="Confirm Drop" :message="`Are you sure you want to drop your ${getDesc('major', major)} major?`" @yes="changes.push({ action: 'drop', field: 'major', value: major })">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action , field }) => action === 'drop' && field === 'major')">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
              </confirm-dialog>
              <major-dialog v-else action="add" :major-items="majorItems">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field }) => action === 'change' && field === 'major')" class="mb-1" @save="({ major: m, advisor, concentrations }) => changes.push({ action: 'change', field: 'major', prev: major, value: m, advisor, concentrations })">
                    <v-icon left>fal fa-plus-circle</v-icon>
                    {{ major === '0000' ? 'Declare major' : 'Add' }}
                  </v-btn>
                </template>
              </major-dialog>
              <v-spacer />
              <v-menu v-if="major !== '0000'">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field, prev }) => action === 'change' && field === 'major' && prev === major)">
                    <v-icon left>fal fa-exchange</v-icon>
                    Change
                  </v-btn>
                </template>
                <v-list>
                  <major-dialog action="change" :major-items="majorItems" @save="({ major: m, advisor, concentrations }) => changes.push({ action: 'change', field: 'major', prev: major, value: m, advisor, concentrations })">
                    <template #activator="{ on }">
                      <v-list-item v-on="on">
                        <v-list-item-title>Change Major</v-list-item-title>
                      </v-list-item>
                    </template>
                  </major-dialog>
                  <advisor-dialog :major="major" @save="(value) => changes.push({ action: 'change', field: 'advisor', prev: advisors, value })">
                    <template #activator="{ on }">
                      <v-list-item v-on="on">
                        <v-list-item-title>Request Advisor Change</v-list-item-title>
                      </v-list-item>
                    </template>
                  </advisor-dialog>
                  <v-list-item v-if="allConcentrations.length > 0">
                    <v-list-item-title>Change Concentrations</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col v-if="current.minors.length > 0">
          <v-card>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Minor{{ current.minors.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ value, text } in current.minors" :key="'minor-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-btn v-if="current.minors.length < 3" :disabled="changes.find(({ action, field }) => action === 'add' && field === 'minor')">
                <v-icon left>fal fa-plus-circle</v-icon>
                Add
              </v-btn>
              <v-spacer />
              <confirm-dialog v-if="current.minors.length === 1" title="Confirm Drop" :message="`Are you sure you want to drop your ${current.minors[0].text} minor?`" @yes="changes.push({ action: 'drop', field: 'minor', value: current.minors[0].value })">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="changes.find(({ action, field }) => action === 'drop' && field === 'minor')">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
              </confirm-dialog>
              <v-menu v-else-if="current.minors.length > 1" v-model="minorDrop">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field}) => action === 'drop' && field === 'minor')">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
                <v-list>
                  <confirm-dialog v-for="{ value, text } in current.minors" :key="value" title="Confirm Drop" :message="`Are you sure you want to drop your ${text} minor?`" @yes="changes.push({ action: 'drop', field: 'minor', value })">
                    <template #activator="{ on }">
                      <v-list-item v-on="on" :disabled="changes.find(({ action, field }) => action === 'drop' && field === 'minor')">
                        <v-list-item-title>Drop {{ text }}</v-list-item-title>
                      </v-list-item>
                    </template>
                  </confirm-dialog>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="includeP12 || isEDSTMajor" class="mb-2">
        <v-card-text>
          <h4>Education or Teacher Certification</h4>
          <template v-if="includeP12">
            <p>Students who desire to teach in a field from their selected major are encouraged to minor in Education followed by completion of the Master of Arts in Teaching degree at Covenant. Note: if you choose not to minor in Education, you are required to complete three pre-requisite education courses: Introduction to Teaching, Education Psychology, and Education of Exceptional Children.</p>
            <v-checkbox :value="P12Secondary" label="P-12 or Secondary (grades 6-12) Education - the five year secondary certification program including MAT degree" readonly />
          </template>
          <template v-if="isEDSTMajor">
            <p>Students who desire middle grades certification should complete the fifth-year Master of Arts in Teaching degree at Covenant. You must also select two content fields in the box below.</p>
            <v-list-item>
              <v-list-item-subtitle>Content Fields</v-list-item-subtitle>
              <v-list-item-title>{{ contentFieldItems.filter(({ value }) => EDSTContentFields.includes(value)).map(({ text }) => text).join('; ') }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-card-text>
      </v-card>
      <v-alert v-for="({ action, field, value, prev }, index) of changes" :key="action + '-' + field + '-' + value" :type="action === 'add' ? 'success' : (action === 'drop' ? 'error' : 'info')">
        {{ action.substring(0, 1).toUpperCase() + action.substring(1) }} {{ field.substring(0, 1).toUpperCase() + field.substring(1) + (action === 'change' ? ` from ${getDesc(field, prev)} to` : '') }}: {{ getDesc(field, value) }}
        <template #append>
          <v-btn @click="changes.splice(index, 1)">Cancel</v-btn>
        </template>
      </v-alert>
      <v-toolbar flat style="margin-bottom:40px" class="mt-4">
        <v-btn text to="/student" class="mr-2">Cancel</v-btn>
        <v-btn color="success" :disabled="!isValid" @click="save">Save</v-btn>
        <v-spacer></v-spacer>
      </v-toolbar>
    </template>
  </div>
</template>
<script>
import { ref, computed, reactive, watch, onMounted } from '@vue/composition-api'
import { inArray } from '@/helpers/functions'

export default {
  components: {
    ConfirmDialog: () => import('@/components/system/confirmDialog'),
    MajorDialog: () => import('@/components/student/program-change/major-dialog'),
    AdvisorDialog: () => import('@/components/student/program-change/advisor-dialog')
  },
  setup (props, { root }) {
    const service = root.$feathers.service('student/major-change')
    const settingServ = root.$feathers.service('student/reg-setting')

    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const roles = computed(() => root.$store.state.roles)
    const required = ref([(v) => !!v || 'Required'])
    const dataLoaded = ref(false)
    const term = ref('')
    const termLabel = ref('')

    const current = reactive({
      programs: [],
      minors: [],
      p12: false,
      contentField: []
    })
    const changes = ref([])

    const submission = ref({})
    const status = computed(() => 'status' in submission.value ? submission.value.status : 'Unsubmitted')
    const original = reactive({})

    const programs = ref([])
    const majorItems = ref([])

    const minor = ref([])
    const minorItems = ref([])
    watch(minor, () => {
      if (minor.value.length > 2) {
        minor.value.splice(2, 1)
      }
    })

    onMounted(async () => {
      if ('Records' in roles.value || 'Employee' in roles.value || 'All Adjuncts' in roles.value || 'Technology Services' in roles.value) {
        root.$router.replace('/student/program-change/admin')
        return
      }
      // Load the major options
      settingServ.find({ query: { form: 'program', category: 'major', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        majorItems.value = data[0].options
      })
      // Load the minor options
      settingServ.find({ query: { form: 'program', category: 'minor', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        minorItems.value = data[0].options
      })
      // Get possible terms from the term warehouse; all terms that have not yet ended
      let { data: termData } = await root.$feathers.service('system/term').find({ query: { end: { $gt: new Date() }, $limit: 50 } })
      let terms = []
      for (let i = 0; i < termData.length; i++) {
        terms.push(termData[i].term)
      }
      // Load the PIDM of the current user
      let pidm = user.value.pidm
      // Check to see if the student has a submission for any of the given terms; if so, we will use that to populate the submission and the original data, in the case where they are making further changes
      const { data: changeData } = await service.find({ query: { pidm, term: { $in: terms } } })
      if (changeData.length > 0) {
        submission.value = changeData[0]
        if (status.value === 'Submitted' || status.value === 'Pending' || status.value === 'Approved') {
          dataLoaded.value = true
          return
        }
      }
      // Load the student's academic data from the student/term-detail for any of the active or future terms, sorted with the term code ascending
      // This will load the current term first, if there is a record for it, or for the next term to begin
      let { data: studentData } = await root.$feathers.service('student/term-detail').find({ query: { pidm, term: { $in: terms }, $sort: { term: 1 } } })
      if (studentData.length > 0) {
        let { academics, term: termCode } = studentData[0]
        term.value = termCode
        const { data: termData } = await root.$feathers.service('system/term').find({ query: { term: termCode } })
        if (termData.length > 0) termLabel.value = termData[0].label
        for (let l in academics) {
          if (Array.isArray(academics[l])) original[l] = academics[l].filter((val) => val !== '')
          else original[l] = academics[l]
        }
        console.log(academics)
        setInitialData(academics)
        dataLoaded.value = true
      } else {
        root.$store.dispatch('main/snackbar', { active: true, color: 'error', timeout: 10000, text: 'No enrollment details were found for you in any active term. Please contact the Records office for assistance.' })
        root.$router.replace('/')
      }
    })

    async function setInitialData ({ major, minor, concentration, advisor }) {
      current.programs = []

      let isFirst = true
      for (const maj of major) {
        const prog = {
          major: maj,
          advisors: [],
          concentrations: [],
          allConcentrations: [],
          allAdvisors: []
        }
        const { data } = await settingServ.find({ query: { form: 'program', major: maj, $sort: { category: 1, term: -1 } } })
        for (const { category, options } of data) {
          if (category === 'advisor') {
            // Find any major advisors in the advisor list and match them to what's in the options list
            prog.availableAdvisors = options
            const arr = options.filter(({ value }) => advisor.find(({ code, pidm }) => code.substr(0, 3) === 'MAJ' && pidm === value))
            if (arr.length === 0) {
              const advrPidm = advisor.find(({ code }) => code === (isFirst ? 'MAJR' : 'MAJ2')).pidm
              const { data: dirData } = await root.$feathers.service('directory/people').find({ query: { pidm: advrPidm } })
              if (dirData.length > 0) {
                prog.advisors = [{ value: advrPidm, text: dirData[0].name.last + ', ' + dirData[0].name.first }]
              }
            } else {
              prog.advisors = arr
            }
          } else if (category === 'concentration') {
            prog.concentrations = options.filter(({ value }) => concentration.includes(value))
            prog.availableConcentrations = options
          }
        }
        current.programs.push(prog)
        isFirst = false
      }
      current.minors = minorItems.value.filter(({ value }) => minor.includes(value))
      console.log(current.minors)
    }

    async function save () {
      if (form.value.validate()) {
        console.log('Form is valid')
      }
      const obj = {
        term: term.value,
        pidm: user.value.pidm,
        bannerId: user.value.bannerId,
        name: user.value.name,
        status: 'Submitted',
        program: programs.value,
        minor: minor.value,
        p12: P12Secondary.value,
        contentFields: EDSTContentFields.value
      }
      const timeline = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: 'Request Submitted',
        icon: 'fal fa-save',
        color: 'info',
        visibleToStudent: true
      }
      console.log(obj)
      let { data } = await service.find({ query: { term: term.value, pidm: user.value.pidm } })
      if (data.length > 0) {
        obj.$push = { timeline }
        await service.patch(data[0]._id, obj)
      } else {
        obj.timeline = [timeline]
        await service.create(obj)
      }
      await root.$feathers.service('system/email').create({
        from: { email: 'report-email@covenant.edu', name: 'Program Change' },
        to: 'recordsoffice@covenant.edu',
        subject: 'Program/Advisor Change Submitted',
        html: user.value.preferred + ' ' + user.value.last + ' has submitted a program/advisor change. Please click on the link below to access the Program/Advisor Change system to review this request.<br/><br/><a href="https://portal.covenant.edu/student/program-change">https://portal.covenant.edu/student/program-change</a>'
      })
      root.$store.dispatch('main/snackbar', { active: true, color: 'success', timeout: 6000, text: 'Your program/advisor change form was submitted successfully.' })
      root.$router.push('/student/program-change/status')
    }

    /** Education add-ons */
    const eduMajors = {
      ART: true,
      BIB: true,
      BIO: true,
      CHE: true,
      COS: true,
      ECO: true,
      ENG: true,
      FRE: true,
      GER: true,
      HIS: true,
      MAT: true,
      MUS: true,
      PHY: true,
      POLS: true,
      SPA: true,
      THET: true
    }
    const includeP12 = computed(() => current.programs.reduce((prev, { major }) => prev || major in eduMajors, false))
    const P12Secondary = ref(false)
    watch(P12Secondary, (val) => {
      if (val && !minor.value.reduce((prev, minr) => prev || minr === 'EDU', false)) {
        minor.value.push('EDU')
      }
    }, {
      lazy: true
    })
    watch(minor, () => {
      if (P12Secondary.value && !inArray('EDU', minor.value)) {
        P12Secondary.value = false
      }
    })

    const isEDSTMajor = computed(() => programs.value.reduce((prev, { major }) => prev || major === 'EDST', false))
    const EDSTContentFields = ref([])
    const contentFieldItems = ref([
      { value: 'EDLA', text: 'Language Arts' },
      { value: 'EDMA', text: 'Mathematics' },
      { value: 'EDSC', text: 'Science' },
      { value: 'EDSS', text: 'Social Studies' }
    ])
    watch(EDSTContentFields, () => {
      if (EDSTContentFields.value.length > 2) {
        EDSTContentFields.value.splice(2, 1)
      }
    })

    const isValid = computed(() => {
      // for (const { major } of programs.value) {
      //   if (major == null || major === '') return false
      // }
      // if (isEDSTMajor.value && EDSTContentFields.value.length < 2) return false
      return true
    })

    const form = ref(null)
    const formValid = ref(false)

    function getDesc (category, value) {
      console.log(category, value)
      let arr = []
      if (category === 'major') {
        arr = majorItems.value
      } else if (category === 'minor') {
        arr = minorItems.value
      } else if (category === 'advisor') {
        for (const { advisors, allAdvisors } of current.programs) {
          if (Array.isArray(value)) {
            const res = advisors.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (res.length > 0) return res.map(({ text }) => text).join('; ')
            const rec = allAdvisors.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (rec) return rec.map(({ text }) => text).join('; ')
          } else {
            const res = advisors.find(({ value: v }) => value === v)
            if (res) return res.text
            const rec = allAdvisors.find(({ value: v }) => value === v)
            if (rec) return rec.text
          }
        }
      } else if (category === 'concentrations') {
        for (const { concentrations, allConcentrations } of current.programs) {
          if (Array.isArray(value)) {
            const res = concentrations.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (res.length > 0) return res.map(({ text }) => text).join('; ')
            const rec = allConcentrations.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (rec) return rec.map(({ text }) => text).join('; ')
          } else {
            const res = concentrations.find(({ value: v }) => value === v)
            if (res) return res.text
            const rec = allConcentrations.find(({ value: v }) => value === v)
            if (rec) return rec.text
          }
        }
      }
      if (arr) {
        if (Array.isArray(value)) {
          return arr.filter(({ value: v }) => value.includes(v)).map(({ text }) => text).join('; ')
        } else {
          const rec = arr.find(({ value: v }) => value === v)
          if (rec) return rec.text
        }
      }
      return value
    }

    return {
      user,
      roles,
      required,
      dataLoaded,
      term,
      termLabel,
      current,
      changes,
      submission,
      status,
      programs,
      majorItems,
      minor,
      minorItems,
      setInitialData,
      save,
      includeP12,
      P12Secondary,
      isEDSTMajor,
      EDSTContentFields,
      contentFieldItems,
      isValid,
      form,
      formValid,
      getDesc
    }
  }
}

// function findDiffs (original, updated) {
//   const rem = original.filter((val) => !inArray(val, updated))
//   const add = updated.filter((val) => !inArray(val, original))
//   return { add, rem }
// }
</script>
